// React Imports
import { Fragment, ReactElement, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";

// Ant Design Imports
import { Button, Form, Input, Radio, Select, Upload } from "antd";
import { MinusCircleOutlined, PlusOutlined } from "@ant-design/icons";
import TextArea from "antd/es/input/TextArea";
// import Dragger from "antd/es/upload/Dragger";

// Component Imports
import BreadcrumbComponent from "../../components/breadcrumb-component/breadcrumb-component";
import CustomInput from "../../components/custom-input/custom-input.component";
import Label from "../../components/label/label.component";

// Other Imports
import instance from "../../store/axios";
import toast from "react-hot-toast";
import { LOADING } from "../../store/actions";
import { ReviewMessageKey } from "../../models/enum";

// Styles
import "./review-form.scss";
import { AxiosResponse } from "axios";
import { onKeyDown } from "src/utils/helper/functions";
import PdfViewer from "src/components/pdf-viewer";
import CustomModal from "src/components/custom-modal/custom-modal";

const { Option } = Select;
const { Dragger } = Upload;

const ReviewForm: React.FC<any> = (): any => {
  const { accountId, kycId, reviewId }: { [key: string]: string | undefined } =
    useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector((state: any) => state.user);
  const [form] = Form.useForm();
  const feedbackMessages = Form.useWatch("feedbackMessages", form);
  const [accountKycDetails, setAccountKycDetails] = useState<any>(null);
  const [actionValue, setActionValue] = useState<string>("accept");
  const [openPdfViewerModal, setOpenPdfViewerModal] = useState<boolean>(false);
  const [selectedDocument, setSelectedDocument] = useState<any>({});
  const [documentsList, setDocumentsList] = useState<any>([]);
  const [items, setItems] = useState<any>({});
  const [riskAssessment, setRiskAssessment] = useState<string>("");

  useEffect(() => {
    getKycApi();
  }, []);

  useEffect(() => {
    if (!accountKycDetails) return;
    if (accountKycDetails?.accountDocumentsId && !accountKycDetails?.kycId) {
      setItems({
        DOCUMNETS: "Documents",
      });
    } else if (
      accountKycDetails.accountDocumentsId &&
      accountKycDetails?.kycId
    ) {
      setItems({
        PERSONAL: "Personal",
        PROFESSIONAL: "Professional",
        COMMUNICATION: "Communication",
        FINANCIAL: "Financial",
        FATCA_CRS: "Fatca_Crs",
        BANK_ACCOUNTS: "Bank_Accounts",
        CONTACT_PERSONS: "Contact_Persons",
        DOCUMNETS: "Documents",
      });
    } else if (accountKycDetails.kycId) {
      setItems({
        PERSONAL: "Personal",
        PROFESSIONAL: "Professional",
        COMMUNICATION: "Communication",
        FINANCIAL: "Financial",
        FATCA_CRS: "Fatca_Crs",
        BANK_ACCOUNTS: "Bank_Accounts",
        CONTACT_PERSONS: "Contact_Persons",
      });
    } else {
      setItems({
        PERSONAL: "Personal",
        PROFESSIONAL: "Professional",
        COMMUNICATION: "Communication",
        FINANCIAL: "Financial",
        FATCA_CRS: "Fatca_Crs",
        BANK_ACCOUNTS: "Bank_Accounts",
        CONTACT_PERSONS: "Contact_Persons",
        DOCUMNETS: "Documents",
      });
    }
  }, [accountKycDetails]);

  useEffect(() => {
    if (accountKycDetails?.account?.pendingDocumentsId) {
      getDocumentsApi();
    }
  }, [accountKycDetails]);

  const getKycApi = async (): Promise<void> => {
    dispatch({ type: LOADING, payload: true });

    try {
      const response: AxiosResponse<any> = await instance.get(
        `/${process.env.REACT_APP_ENDPOINT_VERSION}/kyc-review/${reviewId}`
      );
      setAccountKycDetails(response?.data?.data);
    } catch (error: any) {
      if (error?.response?.status !== 401) {
        toast.error(error?.response?.data?.message || "An error occurred");
      }
      localStorage.setItem(
        "star_prev_url",
        `/customers/review-form/${accountId}/${kycId}/${reviewId}`
      );
    }
    dispatch({ type: LOADING, payload: false });
  };

  const getDocumentsApi = async (): Promise<void> => {
    dispatch({ type: LOADING, payload: true });

    try {
      let response: AxiosResponse<any> = await instance.get(
        `/${process.env.REACT_APP_ENDPOINT_VERSION}/files/account/${accountId}?accountDocumentId=${accountKycDetails?.account?.pendingDocumentsId}`
      );

      setDocumentsList(response.data);
    } catch (error: any) {
      if (error?.response?.status !== 401) {
        toast.error(error?.response?.data?.message || "An error occurred");
      }
      localStorage.setItem(
        "star_prev_url",
        `/customers/review-form/${accountId}/${kycId}/${reviewId}`
      );
    }

    dispatch({ type: LOADING, payload: false });
  };

  const onSubmit = async (values: any) => {
    dispatch({ type: LOADING, payload: true });

    const formData = new FormData();
    if (values?.managementApproval) {
      formData.append("managementApproval", values?.managementApproval?.file);
    }

    if (values?.worldClearance) {
      formData.append("worldClearance", values?.worldClearance?.file);
    }

    if (values?.amlRiskAssessment) {
      formData.append("amlRiskAssessment", values?.amlRiskAssessment?.file);
    }

    if (values?.riskAssessment) {
      formData.append("riskAssessment", values?.riskAssessment);
    }

    if (values?.notes) {
      formData.append("notes", values?.notes);
    }
    if (values?.feedbackMessages?.length) {
      for (let i = 0; i < values?.feedbackMessages?.length; i++) {
        formData.append(
          `feedbackMessages[${i}][key]`,
          values.feedbackMessages[i].key
        );

        if (values.feedbackMessages[i].refrenceId) {
          formData.append(
            `feedbackMessages[${i}][refrenceId]`,
            values.feedbackMessages[i].refrenceId
          );
          formData.append(`feedbackMessages[${i}][refrenceType]`, "document");
        }
        formData.append(
          `feedbackMessages[${i}][message]`,
          values.feedbackMessages[i].message
        );
      }
    }
    try {
      if (values.action === "feedback") {
        await instance.post(
          `/${process.env.REACT_APP_ENDPOINT_VERSION}/kyc-review/${reviewId}/feedback-required`,
          formData,
          {
            headers: { "Content-Type": "multipart/form-data" },
          }
        );
      } else if (values.action === "reject") {
        if (user.kycReviewGroup === "A") {
          await instance.post(
            `/${process.env.REACT_APP_ENDPOINT_VERSION}/kyc-review/${reviewId}/reject`,
            formData,
            {
              headers: { "Content-Type": "multipart/form-data" },
            }
          );
        } else {
          await instance.post(
            `/${process.env.REACT_APP_ENDPOINT_VERSION}/kyc-review/${reviewId}/feedback-required`,
            formData,
            {
              headers: { "Content-Type": "multipart/form-data" },
            }
          );
        }
      } else if (values.action === "skip") {
        formData.append("skipGroupB", "true");
        await instance.post(
          `/${process.env.REACT_APP_ENDPOINT_VERSION}/kyc-review/${reviewId}/approve`,
          formData,
          {
            headers: { "Content-Type": "multipart/form-data" },
          }
        );
      } else {
        await instance.post(
          `/${process.env.REACT_APP_ENDPOINT_VERSION}/kyc-review/${reviewId}/approve`,
          formData,
          {
            headers: { "Content-Type": "multipart/form-data" },
          }
        );
      }
      toast.success("Action submited successfully");
      navigate("/customers/waiting-approvals");
    } catch (error: any) {
      if (error?.response?.status !== 401) {
        toast.error(error?.response?.data?.message || "An error occurred");
      }
      localStorage.setItem(
        "star_prev_url",
        `/customers/review-form/${accountId}/${kycId}/${reviewId}`
      );
    }
    dispatch({ type: LOADING, payload: false });
  };

  const handleChange = (_: any, allValues: any) => {
    setActionValue(allValues?.action);
  };

  const beforeUpload = (file) => {
    const isImageOrPdf =
      file.type === "image/png" ||
      file.type === "image/gif" ||
      file.type === "image/jpg" ||
      file.type === "image/jpeg" ||
      file.type === "application/pdf";

    if (!isImageOrPdf) {
      toast.error(
        "Error: Only PDF and image files are allowed for upload. Please try again with a valid file format!"
      );
    }

    if (!isImageOrPdf) {
      return Upload.LIST_IGNORE;
    }
    return false;
  };

  return accountKycDetails ? (
    <section className="review-form-container flex flex-col">
      <BreadcrumbComponent />
      <div className="element">
        <Form
          form={form}
          onFinish={onSubmit}
          initialValues={{
            action: "accept",
            riskAssessment: accountKycDetails?.riskAssessment,
          }}
          onValuesChange={handleChange}
          scrollToFirstError={true}
          onKeyDown={onKeyDown}
        >
          <div className="flex flex-col md:flex-row flex-wrap items-center mb-5 border-b-[1px] border-gray-900 pb-3">
            <Label text="Action" className="w-full md:w-[35%] mb-0 md:mb-2" />
            <Form.Item name="action">
              <Radio.Group>
                <Radio value="accept">Accept</Radio>
                {user?.kycReviewGroup === "A" &&
                  !accountKycDetails?.isNationalityChanged &&
                  accountKycDetails?.accountDocumentsId &&
                  !accountKycDetails?.kycId && (
                    <Radio value="skip">Accept and skip group (B)</Radio>
                  )}
                {(!accountKycDetails?.account?.accountCode ||
                  user?.kycReviewGroup !== "A") && (
                  <Radio value="reject">Reject</Radio>
                )}
                {user?.kycReviewGroup === "A" && (
                  <Radio value="feedback">Feedback required</Radio>
                )}
              </Radio.Group>
            </Form.Item>
          </div>
          <div className="flex flex-col ">
            {actionValue === "feedback" && (
              <div className="flex flex-col justify-between w-full mb-5 pb-3 border-b-[1px] border-gray-900">
                <Form.List
                  name="feedbackMessages"
                  initialValue={[{ key: null, message: null }]}
                  rules={[
                    {
                      validator: async (_, names) => {
                        if (!names) {
                          return Promise.reject(new Error("At least 1 item"));
                        }
                      },
                    },
                  ]}
                >
                  {(fields: any = 1, { add, remove }, { errors }) => (
                    <Form.Item>
                      <div className="flex flex-col justify-between flex-wrap">
                        <div className="flex justify-between flex-wrap w-full">
                          {fields.map((field: any, index: any) => (
                            <Fragment key={index}>
                              <div className="flex flex-col flex-wrap justify-between w-full">
                                <div className="flex items-start gap-5 flex-wrap w-full">
                                  <div className="flex flex-col w-full md:w-[49%] lg:w-[28%]">
                                    <Label text={"Item"} />
                                    <Form.Item
                                      {...field}
                                      name={[field.name, `key`]}
                                      validateTrigger={["onChange", "onBlur"]}
                                      rules={[
                                        {
                                          required: true,
                                          whitespace: true,
                                          message: "Please input Variable",
                                        },
                                      ]}
                                    >
                                      <Select placeholder="Select Item">
                                        {Object.values(items).map(
                                          (item: string, index: number) => {
                                            return (
                                              <Option value={item} key={index}>
                                                {item}
                                              </Option>
                                            );
                                          }
                                        )}
                                      </Select>
                                    </Form.Item>
                                  </div>
                                  {feedbackMessages?.[index]?.key ===
                                    "Documents" && (
                                    <div className="flex flex-col w-full md:w-[49%] lg:w-[28%]">
                                      <Label text={"Documents list"} />
                                      <Form.Item
                                        {...field}
                                        name={[field.name, `refrenceId`]}
                                        validateTrigger={["onChange", "onBlur"]}
                                        rules={[
                                          {
                                            required: true,
                                            message:
                                              "Please select the document",
                                          },
                                        ]}
                                      >
                                        <Select placeholder="Select Item">
                                          {documentsList.map(
                                            (document: any, index: number) => {
                                              return (
                                                <Option
                                                  value={document.id}
                                                  key={index}
                                                >
                                                  {document?.documentType
                                                    ?.description === "Other"
                                                    ? `${document?.documentType?.description} - ${document?.otherDocDescription}`
                                                    : document?.documentType
                                                        ?.description}
                                                </Option>
                                              );
                                            }
                                          )}
                                        </Select>
                                      </Form.Item>
                                    </div>
                                  )}
                                  <div className="flex flex-col w-full md:w-[49%] lg:w-[28%]">
                                    <Label text={"Note"} />
                                    <Form.Item
                                      {...field}
                                      name={[field.name, `message`]}
                                      validateTrigger={["onChange", "onBlur"]}
                                      rules={[
                                        {
                                          required: true,
                                          whitespace: true,
                                          message: "Please input Variable",
                                        },
                                      ]}
                                    >
                                      <CustomInput placeholder="Enter your note" />
                                    </Form.Item>
                                  </div>
                                </div>

                                {fields.length > 1 ? (
                                  <Button
                                    className="flex items-center w-full justify-end text-red-600 border-0 bg-transparent mb-2 shadow-none p-0"
                                    onClick={() => remove(field.name)}
                                  >
                                    <MinusCircleOutlined />
                                    Delete
                                  </Button>
                                ) : null}
                              </div>
                            </Fragment>
                          ))}
                        </div>
                        <Form.Item>
                          <div className="add-field-button flex justify-start w-full">
                            <Button
                              onClick={() => add()}
                              className=" border-0 text-blue-500 bg-transparent mb-2 shadow-none p-0 text-base"
                              icon={<PlusOutlined />}
                            >
                              Add Item
                            </Button>
                          </div>
                          <Form.ErrorList errors={errors} />
                        </Form.Item>
                      </div>
                    </Form.Item>
                  )}
                </Form.List>
              </div>
            )}
            {/* {actionValue === "reject" && (
              <div className="flex flex-wrap items-center mb-5">
                <Label text="Reject reason" className=" w-full md:w-[20%]" />
                <Form.Item
                  name="statusReason"
                  rules={[
                    {
                      required: true,
                      message: "Please enter the reject reason!",
                    },
                  ]}
                >
                  <TextArea rows={4} placeholder="Enter reject reason" />
                </Form.Item>
              </div>
            )} */}
            <div className="flex items-center mb-9 pb-3 border-b-[1px] border-gray-900">
              <Label text="Risk assessment" className=" w-full md:w-[35%]" />
              <Form.Item
                name="riskAssessment"
                rules={[
                  {
                    required: user?.kycReviewGroup === "B" ? true : false,
                    message: `Please select the risk assessment!`,
                  },
                ]}
              >
                <Radio.Group
                  disabled={user?.kycReviewGroup !== "B"}
                  onChange={(e) => setRiskAssessment(e.target.value)}
                >
                  <Radio value="Low">Low</Radio>
                  <Radio value="Medium">Medium</Radio>
                  <Radio value="High">High</Radio>
                </Radio.Group>
              </Form.Item>
            </div>
          </div>

          <div className="flex flex-wrap items-center">
            <Label text="Notes" className=" w-full md:w-[20%]" />
            <Form.Item name="notes">
              <TextArea rows={4} placeholder="Enter notes" />
            </Form.Item>
          </div>
          <h3 className=" mt-7 mb-5">Attachment</h3>
          <div className="uploads flex flex-col">
            <div className="flex flex-col items-center w-full">
              <div className="flex items-center w-full">
                <Label
                  text="AML risk assessment"
                  className="w-full md:w-[35%]"
                />
                <Form.Item
                  name="amlRiskAssessment"
                  rules={[
                    {
                      required:
                        riskAssessment === "High" &&
                        actionValue === "accept"
                          ? true
                          : false,
                      message: "Please upload the AML risk assessment",
                    },
                  ]}
                >
                  <Dragger
                    beforeUpload={beforeUpload}
                    customRequest={() => false}
                    multiple={false}
                    accept="image/*,.pdf"
                    style={{ background: "transparent", width: "55%" }}
                    disabled={user?.kycReviewGroup === "A"}
                  >
                    <p className="ant-upload-text">
                      {user?.kycReviewGroup === "A"
                        ? " Only compliance and WM control teams are able to upload"
                        : "Click or drag & drop files here"}
                    </p>
                  </Dragger>
                </Form.Item>
              </div>
              {accountKycDetails?.amlRiskAssessment ? (
                <span
                  className="underline cursor-pointer mt-3 text-blue-500"
                  onClick={() => {
                    setSelectedDocument(accountKycDetails?.amlRiskAssessment);
                    setOpenPdfViewerModal(true);
                  }}
                >
                  AML Risk Assessment
                </span>
              ) : (
                ""
              )}
            </div>
            <div className="flex flex-col items-center w-full flex-wrap">
              <div className="flex items-center flex-wrap w-full">
                <Label
                  text="World-check clearance"
                  className="w-full md:w-[35%]"
                />
                <Form.Item
                  name="worldClearance"
                  rules={[
                    {
                      required:
                        user?.kycReviewGroup === "B" &&
                        actionValue === "accept"
                          ? true
                          : false,
                      message: "Please upload the world-check clearance",
                    },
                  ]}
                >
                  <Dragger
                    beforeUpload={beforeUpload}
                    customRequest={() => false}
                    multiple={false}
                    accept="image/*,.pdf"
                    style={{ background: "transparent", width: "55%" }}
                    disabled={user?.kycReviewGroup === "A"}
                  >
                    <p className="ant-upload-text">
                      {user?.kycReviewGroup === "A"
                        ? " Only compliance and WM control teams are able to upload"
                        : "Click or drag & drop files here"}
                    </p>
                  </Dragger>
                </Form.Item>
              </div>
              {accountKycDetails?.worldClearance ? (
                <span
                  className="underline cursor-pointer mt-3 text-blue-500"
                  onClick={() => {
                    setSelectedDocument(accountKycDetails?.worldClearance);
                    setOpenPdfViewerModal(true);
                  }}
                >
                  world Clearance
                </span>
              ) : (
                ""
              )}
            </div>
            <div className="flex flex-col items-center justify-between flex-wrap">
              <div className="flex items-center justify-between flex-wrap w-full">
                <Label
                  text="Management approval"
                  className="w-full md:w-[35%]"
                />
                <Form.Item
                  name="managementApproval"
                  rules={[
                    {
                      required:
                        user?.kycReviewGroup === "B" &&
                        actionValue === "accept"
                          ? true
                          : false,
                      message: "Please upload the management approval",
                    },
                  ]}
                >
                  <Dragger
                    beforeUpload={beforeUpload}
                    customRequest={() => false}
                    multiple={false}
                    accept="image/*,.pdf"
                    style={{ background: "transparent", width: "55%" }}
                    disabled={user?.kycReviewGroup === "A"}
                  >
                    <p className="ant-upload-text">
                      {user?.kycReviewGroup === "A"
                        ? " Only compliance and WM control teams are able to upload"
                        : "Click or drag & drop files here"}
                    </p>
                  </Dragger>
                </Form.Item>
              </div>
              {accountKycDetails?.managementApproval ? (
                <span
                  className="underline cursor-pointer mt-3 text-blue-500"
                  onClick={() => {
                    setSelectedDocument(accountKycDetails?.managementApproval);
                    setOpenPdfViewerModal(true);
                  }}
                >
                  management Approval
                </span>
              ) : (
                ""
              )}
            </div>
          </div>
          <div className="buttons-container flex items-center justify-between">
            <div className="flex underline text-base cursor-pointer">
              <span onClick={() => form.resetFields()}>Reset to default</span>
            </div>
            <div className="flex field items-center justify-between">
              <button
                type="button"
                className="cancel"
                onClick={() => navigate(-1)}
              >
                Cancel
              </button>
              <button type="submit">Submit</button>
            </div>
          </div>
        </Form>
      </div>
      <CustomModal
        isVisible={openPdfViewerModal}
        centered
        width={1100}
        showClose={false}
        onClose={() => {
          setOpenPdfViewerModal(false);
        }}
        className="edit-modal"
      >
        <div className="flex flex-col items-center justify-center">
          <PdfViewer fileUrl={selectedDocument?.storagePath} />
        </div>
      </CustomModal>
    </section>
  ) : (
    ""
  );
};

export default ReviewForm;
